import {
  ActivityStatus,
  FieldTripPlanListResponse,
  FieldTripPlanResponse,
  FieldTripPlanState,
  FieldTripPlanUpdate, Weekdays
} from "@fieldday/fielddayportal-model"
import { CancelRounded, PriorityHighRounded, Report, TaskAltRounded, TimerRounded } from "@mui/icons-material"
import { SvgIconTypeMap } from "@mui/material"
import { OverridableComponent } from "@mui/material/OverridableComponent"
import dayjs, { Dayjs } from "dayjs"
import isBetween from 'dayjs/plugin/isBetween'
import _ from "lodash"
import { OrgType } from '../util/useAPI'
import { hasKey } from "./objectUtil"
import { spaceWords } from "./stringUtil"
dayjs.extend(isBetween)

type stateView = {
  icon?: OverridableComponent<SvgIconTypeMap>
  className?: string
  label?: string
}

export const inactiveFTPStates = [FieldTripPlanState.Canceled, FieldTripPlanState.Completed, FieldTripPlanState.Scheduled]

export function nonprofitStateView(ftp: FieldTripPlanListResponse): stateView | undefined {
  if (ftp.state === FieldTripPlanState.Scheduled && ftp.scheduledActivity?.status === ActivityStatus.Draft) {
    return { icon: PriorityHighRounded, className: "actionRequired", label: "Draft" }
  }
  return { ...nonprofitStateViewMap[ftp.state], label: ftp.state.toLocaleLowerCase() }
}

export const nonprofitStateViewMap: {[key in FieldTripPlanState]?: stateView } = {
  [FieldTripPlanState.Requested]: {
    icon: PriorityHighRounded,
    className: "actionRequired",
  },
  [FieldTripPlanState.Proposed]: {
    icon: TaskAltRounded,
  },
  [FieldTripPlanState.Scheduled]: {
    icon: TaskAltRounded,
  },
  [FieldTripPlanState.Canceled]: {
    icon: CancelRounded,
    className: "dimmed",
  },
  [FieldTripPlanState.Declined]: {
    icon: Report,
    className: "dimmed",
  },
  [FieldTripPlanState.Expired]: {
    icon: TimerRounded,
    className: "dimmed",
  },
  [FieldTripPlanState.Completed]: {
    icon: TaskAltRounded,
    className: "dimmed",
  },
}

export function teamStateView(ftp: FieldTripPlanListResponse): stateView | undefined {
  return { ...teamStateViewMap[ftp.state], label: ftp.state.toLocaleLowerCase() }
}

export const teamStateViewMap: {[key in FieldTripPlanState]?: stateView } = {
  [FieldTripPlanState.Requested]: {
    icon: TaskAltRounded,
  },
  [FieldTripPlanState.Proposed]: {
    icon: PriorityHighRounded,
    className: "actionRequired",
  },
  [FieldTripPlanState.Declined]: {
    icon: Report,
    className: "urgent",
  },
}

export const requestDetailsFields = ["planStartDate", "planEndDate", "daysOfWeek", "timesOfDay", "durationInMinutes", "numberOfSlots"]

export const WeekdaysAsStrings:{[abbrev: string]: number } = {
  Monday: Weekdays.Monday,
  Tuesday: Weekdays.Tuesday,
  Wednesday: Weekdays.Wednesday,
  Thursday: Weekdays.Thursday,
  Friday: Weekdays.Friday,
  Saturday: Weekdays.Saturday,
  Sunday: Weekdays.Sunday,
}

export function durationString(d: number | undefined): string {
  if (!d) return ""
  const h = Math.floor(d / 60)
  const hstr = h ? `${h}h ` : ""
  const m = d - h * 60
  const mstr = m ? `${m}m` : ""
  return `${hstr}${mstr}`.trim()
}

// takes encoded daysOfWeek array
// returns display string of available days
// "Any weekday", "Weekends", "Any day" or sorted list of days, ie: "Monday, Tuesday, Thursday"
export function weekdaysDisplayString(daysOfWeek: number[] | undefined): string {
  const availableDaysAsStrings: string[] = []
  const weekdays = [1, 2, 3, 4, 5]
  const weekends = [6, 7]
  const availableDays = daysOfWeek?.sort()

  if (_.isEqual(availableDays, weekdays)) return "Any weekday"
  if (_.isEqual(availableDays, weekends)) return "Weekends"
  if (_.isEqual(availableDays, _.concat(weekdays, weekends))) return "Any day"

  _.toPairs(WeekdaysAsStrings).forEach(([k, v]) => {
    if (daysOfWeek?.includes(v)) {
      availableDaysAsStrings.push(k)
    }
  })
  return availableDaysAsStrings.join(", ")
}

export function availableDaysAsString(daysOfWeek: number[] | undefined): string {
  const availableDaysAsStrings: string[] = []
  const weekdays = [1, 2, 3, 4, 5]
  const weekends = [6, 7]
  const availableDays = daysOfWeek?.sort()

  if (_.isEqual(availableDays, weekdays)) return "on a weekday "
  if (_.isEqual(availableDays, weekends)) return "on a weekend "
  if (_.isEqual(availableDays, _.concat(weekdays, weekends))) return "any day "

  _.toPairs(WeekdaysAsStrings).forEach(([k, v]) => {
    if (daysOfWeek?.includes(v)) availableDaysAsStrings.push(k)
  })

  if (availableDaysAsStrings.length === 1) return `on a ${availableDaysAsStrings[0]}`

  const lastDay = availableDaysAsStrings.pop()

  return `on a ${availableDaysAsStrings.join(", ")}, or ${lastDay} `
}

export function timesOfDayDisplayString(timesOfDay: string[] | undefined): string {
  return timesOfDay?.map(tod => spaceWords(tod)).join(", ") ?? ""
}

export function dateRangeAsString(startDate: Dayjs, endDate: Dayjs): string {
  const startStr = startDate.year() === endDate.year() ?
          dayjs(startDate).format('MMMM D') :
          dayjs(startDate).format('MMMM D, YYYY')

  const endStr = startDate.year() === endDate.year() && startDate.month() === endDate.month() ?
    dayjs(endDate).format('D') :
    dayjs(endDate).format('MMMM D')

  const year = endDate.year() === dayjs().year() ? "" : `, ${endDate.year()}`

  return `${startStr} - ${endStr}${year}`
}

// takes a FieldTripPlanUpdate object (the newValues field of a TimelineEntryResponse object)
// decodes each change to one of the requestDetailsFields into human readable format
// returns changes as an array of tuples [key, value] for display by the TimelineEntryChangesDisplay component
export function requestEditsDecoder(newValues: FieldTripPlanUpdate | undefined): Array<[string, string]> {
  const decodedEdits: Array<[string, string]> = []

  const fieldnamesDecoded = {
    daysOfWeek: "Available days",
    timesOfDay: "Preferred time of day",
    durationInMinutes: "Field Trip duration",
    numberOfSlots: "Number of volunteers",
  }

  const valuesDecoder = {
    daysOfWeek: weekdaysDisplayString,
    timesOfDay: timesOfDayDisplayString,
    durationInMinutes: durationString,
    numberOfSlots: (n: number) => n.toLocaleString(),
  }

  if (_.has(newValues, "planStartDate") && _.has(newValues, "planEndDate")) {
    const k = "Preferred date range"
    const startDate = dayjs(newValues?.planStartDate).format("MMM D")
    const endDate = dayjs(newValues?.planEndDate).format("MMM D")
    const v = `${startDate} - ${endDate}`
    decodedEdits.push([k, v])
  } else if (_.has(newValues, "planStartDate")) {
    const k = "Preferred date range start"
    const startDate = dayjs(newValues?.planStartDate).format("MMM D")
    decodedEdits.push([k, startDate])
  } else if (_.has(newValues, "planEndDate")) {
    const k = "Preferred date range end"
    const endDate = dayjs(newValues?.planEndDate).format("MMM D")
    decodedEdits.push([k, endDate])
  }

  const requestEdits = _.toPairs(newValues).filter(([key, value]) => _.includes(requestDetailsFields, key))

  requestEdits.forEach(([k, v]) => {
    if (hasKey(fieldnamesDecoded, k)) {
      const decodedKey = fieldnamesDecoded[k]
      const decodeValue = valuesDecoder[k]
      decodedEdits.push([decodedKey, decodeValue(v)])
    }
  })

  return decodedEdits
}

export interface DateConditions {
  withinRange: boolean
  availableDay: boolean
  past: boolean
}

export function getDateConditions(day: dayjs.Dayjs, ftp: FieldTripPlanResponse | null): DateConditions {
  const past = day.isBefore(dayjs(), "day")
  const withinRange = dayjs(day).isBetween(ftp?.planStartDate, ftp?.planEndDate, null, "[]")
  const availableDays = ftp?.daysOfWeek?.map(d => d === 7 ? 0 : d) ?? []
  const availableDay = availableDays.includes(day.day())
  return { withinRange, availableDay, past }
}

export const OrgTurnStates: {[K in OrgType]: FieldTripPlanState[] } = {
  "nonprofit": [ FieldTripPlanState.Requested ],
  "orgunit": [
    FieldTripPlanState.Proposed,
    FieldTripPlanState.Declined,
  ]
}
